import { useCallback, useEffect, useRef } from "react";
import { Archive, ClipboardList, Headset, LogOut, MessageSquareHeart, Users } from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth, api } from "@/auth/AuthContext";
import "@/pages/laksmi/Laksmi.css";

const NAV = {
  master: [["/laksmi", "Kelola Konsultasi", Headset], ["/laksmi/tamu", "Data Tamu", Users], ["/laksmi/riwayat", "Riwayat", ClipboardList]],
  admin: [["/laksmi", "Kelola Konsultasi", Headset], ["/laksmi/tamu", "Data Tamu", Users], ["/laksmi/riwayat", "Riwayat", ClipboardList]],
  pt: [["/laksmi", "Konsultasi", MessageSquareHeart]],
};

const URGENT_MS = 30000;     // joined tamu tanpa status / menunggu verifikasi -> tiap 30 detik
const ACTIVE_MS = 900000;    // sudah diberi status tapi belum "Selesai" -> tiap 15 menit

// Notifikasi Admin berjalan di level layout sehingga tetap berbunyi walau Admin membuka menu LAKSMI lain.
function useLaksmiAdminChime(active) {
  const audio = useRef(null);
  const known = useRef(new Set());
  const lastUrgent = useRef(0);
  const lastActive = useRef(0);

  const play = useCallback(() => {
    if (!audio.current) return;
    audio.current.currentTime = 0;
    audio.current.play().catch(() => {});
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    audio.current = new Audio("/laksmi-chime.mp3");
    const unlock = () => {
      const a = audio.current;
      if (a) a.play().then(() => { a.pause(); a.currentTime = 0; }).catch(() => {});
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);

    const tick = async () => {
      let list = [];
      try { const res = await api.get("/laksmi/admin/guests"); list = res.data.guests || []; }
      catch (_) { return; }
      const urgent = list.some((g) => (g.joined && !g.admin_status) || (!g.is_login && g.verification === "pending"));
      const stillOpen = list.some((g) => g.joined && (g.admin_status === "menunggu" || g.admin_status === "tidak_di_zoom"));
      const hasNew = list.some((g) => !known.current.has(g.guest_id));
      const now = Date.now();
      if (hasNew && (urgent || stillOpen)) { play(); lastUrgent.current = now; lastActive.current = now; }
      if (urgent && now - lastUrgent.current >= URGENT_MS) { play(); lastUrgent.current = now; }
      if (stillOpen && now - lastActive.current >= ACTIVE_MS) { play(); lastActive.current = now; }
      known.current = new Set(list.map((g) => g.guest_id));
    };
    tick();
    const t = setInterval(tick, 10000);
    return () => {
      clearInterval(t);
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [active, play]);
}

export default function LaksmiLayout() {
  const { user, logout } = useAuth();
  const links = NAV[user?.role] || [];
  const canAccessPadma = ["master", "admin", "pt"].includes(user?.role);
  useLaksmiAdminChime(["master", "admin"].includes(user?.role));
  return (
    <div className="laksmi-shell" data-testid="laksmi-layout">
      <header className="laksmi-header">
        <div className="laksmi-brand">
          {canAccessPadma ? <NavLink to="/padma" className="laksmi-logo-link" title="Akses Layanan PADMA" data-testid="laksmi-padma-home"><img src="/logo-padma-transparent.png" alt="Logo PADMA" /></NavLink> : <img src="/logo-padma-transparent.png" alt="Logo PADMA" />}
          <div><div className="laksmi-brand-title"><span>LAKSMI</span><i>PADMA</i></div><p>Layanan Konsultasi Kemahasiswaan Perguruan Tinggi</p></div>
        </div>
        <div className="laksmi-user">
          <span>{user?.namapt || user?.display_name}</span>
          <NavLink to="/padma" data-testid="laksmi-back-padma"><Archive size={14} /> PADMA</NavLink>
          <button onClick={logout} data-testid="laksmi-logout" title="Keluar"><LogOut size={16} /></button>
        </div>
      </header>
      {links.length > 1 && <nav className="laksmi-nav" aria-label="Navigasi LAKSMI">
        {links.map(([to, label, Icon]) => <NavLink key={to} to={to} end={to === "/laksmi"} data-testid={`laksmi-nav-${to.split("/").pop() || "home"}`} className={({ isActive }) => isActive ? "active" : ""}><Icon size={16} /> {label}</NavLink>)}
      </nav>}
      <main className="laksmi-main"><Outlet /></main>
    </div>
  );
}
